import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { cn } from '@rekrytering/ui/lib/utils'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { env } from '@/env.client'
import { authClient } from '@/lib/auth-client'

type MessageRole = 'user' | 'assistant'

type ChatMessage = {
  readonly role: MessageRole
  readonly text: string
  readonly id: string
}

type StreamingState =
  | { readonly status: 'idle' }
  | { readonly status: 'streaming' }
  | { readonly status: 'error'; readonly reason: string }

type AuthClientWithConvex = typeof authClient & {
  convex: {
    token: (opts: {
      fetchOptions: { throw: boolean }
    }) => Promise<{ data?: { token?: string } | null }>
  }
}

const fetchConvexToken = async (): Promise<string | null> => {
  const client = authClient as AuthClientWithConvex
  const result = await client.convex.token({ fetchOptions: { throw: false } })
  return result?.data?.token ?? null
}

const parseSSEChunk = (chunk: string): string => {
  let text = ''
  for (const line of chunk.split('\n')) {
    if (!line.startsWith('data: ')) continue
    const data = line.slice(6).trim()
    if (data === '[DONE]') continue
    try {
      const parsed = JSON.parse(data) as { text?: string }
      if (typeof parsed.text === 'string') text += parsed.text
    } catch (_) {
      // Malformed JSON in SSE chunk — skip
    }
  }
  return text
}

const MessageBubble = ({
  message,
  isStreaming,
}: {
  message: ChatMessage
  isStreaming: boolean
}) => (
  <div
    className={cn(
      'flex flex-col gap-1',
      message.role === 'user' ? 'items-end' : 'items-start',
    )}
  >
    <div
      className={cn(
        'max-w-[80%] rounded-lg px-3 py-2 text-sm leading-relaxed',
        message.role === 'user'
          ? 'bg-primary text-primary-foreground'
          : 'bg-muted text-primary',
      )}
    >
      {message.text}
      {isStreaming && message.role === 'assistant' && (
        <span className="ml-1 inline-block h-3 w-1 animate-pulse bg-current opacity-70" />
      )}
    </div>
  </div>
)

export const RagChatPanel = ({
  interviewId,
}: {
  interviewId: Id<'interviews'>
}) => {
  const { t } = useTranslation()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [streamingState, setStreamingState] = useState<StreamingState>({
    status: 'idle',
  })
  const scrollRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  const scrollToBottom = useCallback((): void => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [])

  const sendMessage = useCallback(async (): Promise<void> => {
    const message = inputValue.trim()
    if (!message || streamingState.status === 'streaming') return

    const userMessageId = crypto.randomUUID()
    const assistantMessageId = crypto.randomUUID()

    setMessages((prev) => [
      ...prev,
      { role: 'user', text: message, id: userMessageId },
      { role: 'assistant', text: '', id: assistantMessageId },
    ])
    setInputValue('')
    setStreamingState({ status: 'streaming' })
    scrollToBottom()

    const token = await fetchConvexToken()
    if (!token) {
      setStreamingState({ status: 'error', reason: 'auth_failed' })
      setMessages((prev) => prev.filter((m) => m.id !== assistantMessageId))
      return
    }

    const controller = new AbortController()
    abortRef.current = controller

    try {
      const response = await fetch(`${env.VITE_CONVEX_SITE_URL}/rag/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ interviewId, message }),
        signal: controller.signal,
      })

      if (!response.ok || !response.body) {
        throw new Error('bad_response')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        const newText = parseSSEChunk(chunk)

        if (newText) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessageId
                ? { ...m, text: m.text + newText }
                : m,
            ),
          )
          scrollToBottom()
        }
      }

      setStreamingState({ status: 'idle' })
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setStreamingState({ status: 'idle' })
        return
      }
      setStreamingState({ status: 'error', reason: 'stream_failed' })
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMessageId
            ? { ...m, text: t('interviews.chat.errorMessage') }
            : m,
        ),
      )
    }
  }, [inputValue, streamingState.status, interviewId, scrollToBottom, t])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void sendMessage()
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/10 p-4">
      <div
        ref={scrollRef}
        className="flex max-h-80 min-h-32 flex-col gap-3 overflow-y-auto"
      >
        {messages.length === 0 && (
          <p className="text-sm text-primary/40 text-center mt-8">
            {t('interviews.chat.placeholder')}
          </p>
        )}
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            isStreaming={
              streamingState.status === 'streaming' &&
              message.role === 'assistant' &&
              message.id === messages[messages.length - 1]?.id
            }
          />
        ))}
      </div>

      <div className="flex gap-2">
        <textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('interviews.chat.inputPlaceholder')}
          disabled={streamingState.status === 'streaming'}
          rows={2}
          className="flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-primary placeholder:text-primary/40 focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        />
        <Button
          onClick={() => void sendMessage()}
          disabled={!inputValue.trim() || streamingState.status === 'streaming'}
          size="sm"
          className="self-end"
        >
          {streamingState.status === 'streaming'
            ? t('interviews.chat.sending')
            : t('interviews.chat.send')}
        </Button>
      </div>
    </div>
  )
}
